import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import FormatCurrency from 'components/Common/FormatCurrency'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useAppDispatch } from 'hooks/reduxHooks'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import IconLoadingBox from 'LuxKit/Icons/IconLoadingBox'
import LuxPlusBenefitsListLoadingSkeleton from '../LuxPlusBenefitsListLoadingSkeleton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LuxPlusSubscriptionCancellationModal from '../LuxPlusSubscriptionCancellationModal'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import offerPageURL from 'lib/offer/offerPageURL'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LuxPlusBenefitsList from '../LuxPlusBenefitsList'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import LuxPlusSubscriptionTsAndCs from '../LuxPlusSubscriptionTsAndCs'
import LineLockIcon from 'LuxKit/Icons/line/LineLockIcon'
import { connect } from 'react-redux'
import { isBedbankOffer, isLEOffer, isTourV2Offer, isCruiseOffer } from 'lib/offer/offerTypes'
import { formatInclusionsTitleLuxPlus } from 'constants/inclusions'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import useLuxPlusInsuranceAvailableCopy from 'checkout/hooks/useLuxPlusInsuranceAvailableCopy'
import useLuxPlusBookingProtectionAvailableCopy from 'checkout/hooks/useLuxPlusBookingProtectionAvailableCopy'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import useEnabledLuxPlusBenefits from 'luxPlus/hooks/useEnabledLuxPlusBenefits'
import { earlyAccessRestrictedToMembers } from 'luxPlus/lib/utils'

type InheritedModalProps = Pick<React.ComponentProps<typeof Modal>,
  'onPrimaryActionClick' |
  'primaryActionText' |
  'isSecondaryActionHidden' |
  'isPrimaryActionHidden'
>

interface MappedProps {
  subscription: App.ProductSubscription
}

interface Props extends MappedProps, InheritedModalProps {
  title?: string
  subTitle?: React.ReactNode
  offer?: App.Offer | App.OfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | App.CruiseOffer | App.BedbankOffer | App.BedbankOfferSummary
  saveAmount?: number
  type?: App.LuxPlusLabelModalType
  hideSaveAmountPlus?: boolean
  providerPage?: App.LuxPlusLabelPage
  inclusions?: Array<App.PackageInclusion>
  tileInclusions?: Array<App.OfferInclusion>
}

interface Copy {
  title: string
  subTitle: React.ReactNode
  firstBenefit?: string
  saveAmountBenefit?: string
  insuranceBenefit?: string
}

function LuxPlusBenefitsModal(props: Props) {
  const {
    offer,
    saveAmount,
    type,
    subscription,
    hideSaveAmountPlus,
    providerPage,
    inclusions,
    tileInclusions,
  } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { resolve } = useModalElementContext()
  const isLuxPlusLandingPage = providerPage === 'landing-page'
  const isOfferPage = providerPage === 'offer-page'

  const offerUrl = offerPageURL(offer)
  const geo = useContext(GeoContext)
  const formattedSaveAmount = formatCurrency(saveAmount ?? 0, geo.currentCurrency, geo.currentRegionCode, { format: 'roundedDollar' })
  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'
  const insuranceAvailableCopy = useLuxPlusInsuranceAvailableCopy()
  const bookingProtectionCopy = useLuxPlusBookingProtectionAvailableCopy()
  const enabledBenefits = useEnabledLuxPlusBenefits(subscription.offers?.[0].benefits)
  const isEarlyAccessRestrictedToMembers = earlyAccessRestrictedToMembers(offer)

  const onShowCancellationPolicy = useCallback(() => {
    showModal(<LuxPlusSubscriptionCancellationModal offer={subscription.offers?.[0]} />)
  }, [showModal, subscription.offers])

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  const thisHotelLabel = intl.formatMessage({
    id: 'u1gjulOA2b',
    defaultMessage: 'this hotel',
    description: 'Generic fallback for hotel name in LuxPlus+ benefits modal subtitle',
  })
  const thisTourLabel = intl.formatMessage({
    id: 'y0InmSD5RP',
    defaultMessage: 'this tour',
    description: 'Generic fallback for tour name in LuxPlus+ benefits modal subtitle',
  })
  const thisCruiseLabel = intl.formatMessage({
    id: '41oksZXZRE',
    defaultMessage: 'this cruise',
    description: 'Generic fallback for cruise name in LuxPlus+ benefits modal subtitle',
  })

  const copy = useMemo<Copy>(() => {
    switch (type) {
      case 'early-access-offer':
        return {
          title: intl.formatMessage({
            id: 'mBCZCTfWIo',
            defaultMessage: 'Join {programName} to get early access to this offer and more!',
            description: 'Benefits modal title for early access offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          subTitle: <FormattedMessage
            id="XS06FMY/RY"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isLEOffer(offer) && !isEarlyAccessRestrictedToMembers ? <b>{offer?.property?.name}</b> : thisHotelLabel,
            }}
          />,
          firstBenefit: intl.formatMessage({
            id: '0zDzBicVEM',
            defaultMessage: 'Secure your room and dates before the general public',
            description: 'Benefit text about early access to rooms and dates before non-members',
          }),
          saveAmountBenefit: intl.formatMessage({
            id: 'Nm2DjrBRXG',
            defaultMessage: 'Save {saveAmount} on this booking with member pricing',
            description: 'Benefit text showing savings amount with member pricing on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'member-price-offer':
        return {
          title: intl.formatMessage({
            id: 'ZJVPdqrh3T',
            defaultMessage: 'Join {programName} to save {saveAmount} on this offer and more!',
            description: 'Benefits modal title showing savings amount for member pricing offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, saveAmount: formattedSaveAmount + saveAmountSuffix }),
          subTitle: <FormattedMessage
            id="XS06FMY/RY"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isLEOffer(offer) ? <b>{offer?.property?.name}</b> : thisHotelLabel,
            }}
          />,
          saveAmountBenefit: intl.formatMessage({
            id: 'anVYJj0HDi',
            defaultMessage: 'Save {saveAmount} on this booking',
            description: 'Benefit text showing savings amount on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'member-price-offer-lpp':
        return {
          title: intl.formatMessage({
            id: 'ZJVPdqrh3T',
            defaultMessage: 'Join {programName} to save {saveAmount} on this offer and more!',
            description: 'Benefits modal title showing savings amount for member pricing offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, saveAmount: formattedSaveAmount + saveAmountSuffix }),
          subTitle: <FormattedMessage
            id="XS06FMY/RY"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isBedbankOffer(offer) ? <b>{offer?.name}</b> : thisHotelLabel,
            }}
          />,
          saveAmountBenefit: intl.formatMessage({
            id: 'anVYJj0HDi',
            defaultMessage: 'Save {saveAmount} on this booking',
            description: 'Benefit text showing savings amount on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'member-price-offer-lpp-no-save-amount':
        return {
          title: intl.formatMessage({
            id: '5/t+Et7h+w',
            defaultMessage: '{programName} discounts available on this offer and more!',
            description: 'Benefits modal title when no specific save amount shown for bedbank offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          subTitle: <FormattedMessage
            id="XS06FMY/RY"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isBedbankOffer(offer) ? <b>{offer?.name}</b> : thisHotelLabel,
            }}
          />,
        }
      case 'bonus-inclusions':
      case 'member-inclusions':
        return {
          title: intl.formatMessage({
            id: '9ynBLtcHKX',
            defaultMessage: 'Join {programName} to enjoy {inclusionsTitle} for this offer and more!',
            description: 'Benefits modal title for bonus inclusions offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, inclusionsTitle: formatInclusionsTitleLuxPlus(intl) }),
          subTitle: <FormattedMessage
            id="SIUsOjgftj"
            defaultMessage="Become a member of our VIP travel club for {price} per year to unlock {inclusionsTitle} when you book {offerName}."
            description="Subtitle encouraging user to join VIP travel club to unlock bonus inclusions for a specific offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              inclusionsTitle: formatInclusionsTitleLuxPlus(intl),
              offerName: isLEOffer(offer) ? <b>{offer?.property?.name}</b> : isCruiseOffer(offer) ? thisCruiseLabel : thisHotelLabel,
            }}
          />,
        }
      case 'member-price-offer-tour':
        return {
          title: intl.formatMessage({
            id: 'E94k4pBvu0',
            defaultMessage: 'Join {programName} to save {saveAmount}/person on this offer and more!',
            description: 'Benefits modal title showing per-person savings for tour offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, saveAmount: formattedSaveAmount + saveAmountSuffix }),
          subTitle: <FormattedMessage
            id="XS06FMY/RY"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isTourV2Offer(offer) ? <b>{offer?.name}</b> : thisTourLabel,
            }}
          />,
          saveAmountBenefit: intl.formatMessage({
            id: 'tAmfBjrT4f',
            defaultMessage: 'Save {saveAmount}/person on this booking',
            description: 'Benefit text showing per-person savings on current tour booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'options-available-tour':
        return {
          title: intl.formatMessage({
            id: 'jIcg1tCJ8s',
            defaultMessage: 'Join {programName} to save on select tour options on this offer and more!',
            description: 'Benefits modal title for tours with LuxPlus+ pricing options available',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          subTitle: <FormattedMessage
            id="XS06FMY/RY"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isTourV2Offer(offer) ? <b>{offer?.name}</b> : thisTourLabel,
            }}
          />,
        }
      default:
        return {
          title: props.title ?? '',
          subTitle: props.subTitle ?? '',
          saveAmountBenefit: formattedSaveAmount ? intl.formatMessage({
            id: 'Nm2DjrBRXG',
            defaultMessage: 'Save {saveAmount} on this booking with member pricing',
            description: 'Benefit text showing savings amount with member pricing on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }) : '',
        }
    }
  }, [formattedSaveAmount, intl, offer, props.subTitle, props.title, saveAmountSuffix, subscription.offers, type, isEarlyAccessRestrictedToMembers, thisHotelLabel, thisTourLabel, thisCruiseLabel])

  const viewOfferLabel = intl.formatMessage({
    id: 'j7U3vA19qG',
    defaultMessage: 'View offer',
    description: 'Primary action button text to view the offer in LuxPlus+ benefits modal',
  })
  const learnMoreLabel = intl.formatMessage({
    id: '8EKWPyeHNL',
    defaultMessage: 'Learn more about {programName}',
    description: 'Secondary action button text to learn more about LuxPlus+ membership',
  }, { programName: LUXURY_PLUS.PROGRAM_NAME })

  return <Modal
    primaryActionText={props.primaryActionText ?? viewOfferLabel}
    isPrimaryActionHidden={props.isPrimaryActionHidden || !offerUrl || isOfferPage}
    primaryActionTo={props.onPrimaryActionClick ? undefined : offerUrl}
    isSecondaryActionHidden={props.isSecondaryActionHidden ?? isLuxPlusLandingPage}
    secondaryActionText={learnMoreLabel}
    secondaryActionTo={`/${LUXURY_PLUS_PATHS.LANDING_PAGE}`}
    title={copy.title}
    onPrimaryActionClick={props.onPrimaryActionClick}
  >
    {subscription.error && <BodyText variant="medium">{intl.formatMessage({
      id: 'bNDEh6UVDF',
      defaultMessage: 'There was an error loading the price details. Please try again later.',
      description: 'Error message when LuxPlus+ subscription pricing fails to load in benefits modal',
    })}
    </BodyText>}
    {subscription.fetching &&
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium">
            <TextLoadingBox width="100%" />
            <TextLoadingBox width="40ch" />
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="medium" startIcon={<IconLoadingBox colour="highlight-secondary" />}>
              <TextLoadingBox colour="highlight-secondary" width="16ch" />
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<IconLoadingBox colour="highlight-secondary" />}>
              <TextLoadingBox colour="highlight-secondary" width="24ch" />
            </BodyTextBlock>
          </Group>
          <BodyText variant="medium">
            <TextLoadingBox width="40ch" />
          </BodyText>
          <LuxPlusBenefitsListLoadingSkeleton benefitsCounts={6} />
          <BodyTextBlock variant="medium" endIcon={<IconLoadingBox />}>
            <TextLoadingBox colour="highlight-secondary" width="16ch" />
          </BodyTextBlock>
        </Group>
        <BodyText variant="small">
          <TextLoadingBox width="100%" />
          <TextLoadingBox width="24ch" />
        </BodyText>
      </Group>}
    {subscription.offers &&
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          {copy.subTitle && <BodyText variant="medium">
            {copy.subTitle}
          </BodyText>}
          <Group direction="vertical" gap={8}>
            {copy.firstBenefit && <BodyTextBlock variant="medium" weight="bold" colour="highlight-secondary" startIcon={<LineLockIcon />}>
              {copy.firstBenefit}
            </BodyTextBlock>}
            {!!saveAmount && copy.saveAmountBenefit && <BodyTextBlock variant="medium" weight="bold" colour="highlight-secondary" startIcon={<LineUsdCircleIcon />}>
              {copy.saveAmountBenefit}
            </BodyTextBlock>}
            {inclusions?.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} colour="highlight-secondary" isHighlighted />)}
            {tileInclusions?.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} colour="highlight-secondary" isHighlighted />)}
            {insuranceAvailableCopy && <BodyTextBlock variant="medium" weight="bold" colour="highlight-secondary" startIcon={<LineShieldCheckIcon />}>
              {insuranceAvailableCopy}
            </BodyTextBlock>}
            {bookingProtectionCopy && <BodyTextBlock variant="medium" weight="bold" colour="highlight-secondary" startIcon={<LineShieldCheckIcon />}>
              {bookingProtectionCopy}
            </BodyTextBlock>}
            <BodyTextBlock variant="medium" weight="bold" colour="highlight-secondary" startIcon={<LineStarIcon />}>
              <FormattedMessage
                id="ITcfmkJPp9"
                defaultMessage="Special offer: No one-off joining fee if you book today (save {joinFee})"
                description="Special offer banner showing waived joining fee savings in LuxPlus+ benefits modal"
                values={{ joinFee: <FormatCurrency format="roundedDollar" value={subscription.offers[0].joinFee} /> }}
              />
            </BodyTextBlock>
          </Group>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: '8H7ySSEp3O',
              defaultMessage: 'You’ll also enjoy these unlimited benefits all year round:',
              description: 'Section heading for unlimited yearly benefits in LuxPlus+ benefits modal',
            })}
          </BodyText>
          <LuxPlusBenefitsList kind="long" benefits={enabledBenefits} />
          <TextLink weight="bold" colour="highlight-secondary" size="medium" endIcon={<LineInfoCircleIcon colour="neutral-one" />} onClick={onShowCancellationPolicy}>
            {intl.formatMessage({
              id: 'PAF3hAXTod',
              defaultMessage: 'Cancel renewal anytime',
              description: 'Link text to view cancellation policy in LuxPlus+ benefits modal',
            })}
          </TextLink>
        </Group>
        <LuxPlusSubscriptionTsAndCs onTsAndCsLinkClick={resolve} type={offer?.luxPlus.access === 'memberOnly' ? undefined : 'withPrice'} />
      </Group>}
  </Modal>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  subscription: state.luxPlus.products.subscription,
})

export default connect(mapStateToProps)(LuxPlusBenefitsModal)
